/**
 * RegieTestCandidatePage — page PUBLIQUE (jeton dans l'URL, aucun
 * `<AuthGate>`, cf. `App.tsx` — même patron que `/docs`). Orchestre les
 * écrans de la candidature via une petite machine d'état locale :
 *
 *   parcours PUBLIC   profil → accusé de réception   ← et ça s'arrête là
 *   lien de Discord   ?test=1 → [mur PC] → vérif technique → intro → épreuve → fin
 *
 * 🎬 L'INTRO EST ARRIVÉE LE 06-10-2026. Kayla : « une sorte d'introduction où
 * il faut qu'ils apprennent, tu peux déjà mettre la vidéo que j'avais faite.
 * Puis ensuite on les met en conditions réelles. » Elle se place APRÈS la
 * vérif technique (qui reste la mesure de l'appareil et de la connexion) et
 * AVANT la simulation, et elle se rejoue à chaque réouverture du lien : elle
 * se lit en deux minutes, et l'épreuve repart du début de toute façon.
 *
 * 🩸 L'ORDRE A CHANGÉ LE 19-08-2026, ET C'EST TOUT L'OBJET DE CE FICHIER.
 * Il était `vérif technique → profil → épreuve`, avec le mur PC placé AVANT
 * tout le reste. La landing crée le jeton et redirige aussitôt sur
 * `/regie-test/<jeton>` : un candidat qui arrivait du téléphone — c'est-à-dire
 * le CAS NORMAL, la publicité tourne sur mobile — tombait donc sur le mur
 * avant qu'on ne lui ait demandé quoi que ce soit. S'il fermait l'onglet, son
 * lien était perdu pour toujours et on n'avait même pas son adresse : le lead
 * disparaissait sans laisser de trace. Kayla, le 19-08 : « on perd toutes les
 * leads parce qu'ils sont sur téléphone ».
 *
 * Le formulaire passe donc EN PREMIER, et il se remplit très bien sur un
 * téléphone. Le mur ne se lève qu'après, quand il ne reste plus que l'épreuve
 * — qui, elle, exige vraiment un ordinateur (cf. `useEstOrdinateur`). À ce
 * moment-là le candidat est en base, avec son e-mail : on peut le relancer.
 *
 * 🩸 ET IL A RECHANGÉ LE MÊME JOUR, L'APRÈS-MIDI : le formulaire n'ouvre plus
 * l'épreuve DU TOUT, sur aucun appareil. Kayla : « une fois qu'il a rempli le
 * formulaire, on l'invite dans l'organisation. Il a juste une page qui lui dit
 * qu'une invitation lui a été envoyée […] et c'est sur Discord que je vais
 * mettre le lien du test. Ils seront obligés de le consulter sur Discord pour
 * pouvoir faire le test. » Le parcours public se termine donc sur
 * `EcranCandidatureDeposee`, et l'épreuve n'est atteignable que par le lien
 * marqué `?test=1` (cf. `MARQUEUR_TEST`).
 *
 * ⚠️ Conséquence à ne pas découvrir en prod : SANS ce marqueur, rouvrir son
 * lien redonne l'accusé de réception. Le mur PC et la vérif technique ne sont
 * donc plus sur le chemin de personne tant que Kayla n'a pas posté le lien.
 *
 * ⚠️ CE QUI RESTE SUR PC, ET POURQUOI : la vérif technique mesure la machine
 * sur laquelle il travaillera. La faire passer avant le mur la ferait mesurer
 * le téléphone, ce qui ne dit rien. Elle reste donc APRÈS le mur — et comme
 * elle n'est plus la première porte, une reprise doit savoir si elle a déjà
 * eu lieu : c'est `device_class`, que `GET /state` expose et que seul
 * `POST /device` renseigne. Sans cette relecture, tout candidat venu du
 * mobile serait passé directement à l'épreuve et on n'aurait JAMAIS mesuré sa
 * connexion — une donnée perdue en silence, pour la majorité des candidats.
 *
 * 🪤 ET AUCUN EFFET DE BORD DEPUIS UN TÉLÉPHONE. L'ouverture de l'épreuve
 * (`POST /session` quand il n'y en a pas en cours) est gardée par
 * `estOrdinateur`. Sans cette garde, chaque réouverture du lien sur mobile
 * partait taper le serveur pour rien — et `POST /session` rend **409** quand
 * une session est déjà en cours (cf. `can_start_new_session`), ce que ce
 * fichier traite comme une erreur : le candidat aurait lu « Ce lien n'est
 * plus valide » sur son téléphone alors que son lien était parfaitement bon.
 *
 * Le backend n'a PAS changé de contrat, et n'avait pas besoin de changer :
 * `POST /profile` est idempotent (il gèle la session 1 une seule fois) et le
 * jeton n'expire jamais (`require_candidate` ne rend 404 que sur un lien
 * absent ou révoqué). Remplir le formulaire sur son téléphone puis ouvrir le
 * lien sur son ordinateur trois jours plus tard fonctionne donc de soi.
 */
import { useEffect, useReducer, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { useAction } from "convex/react";

import { api as convexApi } from "@/../convex/_generated/api";
import { ApiError } from "@/core/api/client";
import { Button, Spinner, Vide } from "@/shared/ui";
import { AlertCircle, CheckCircle2 } from "@/shared/ui/icons";

import { candidateApi, IS_MOCK_MODE } from "./api";
import { initPixel, trackPixel } from "./pixel";
import { EndScreen } from "./candidate/EndScreen";
import { CadreDuPoste, PhoneFrame } from "./candidate/PhoneFrame";
import { EcranCandidatureDeposee } from "./candidate/EcranCandidatureDeposee";
import { EcranOrdinateur } from "./candidate/EcranOrdinateur";
import { useEstOrdinateur } from "./candidate/useEstOrdinateur";
import { ProfileFormScreen } from "./candidate/ProfileFormScreen";
import { TechCheckScreen } from "./candidate/TechCheckScreen";
import { IntroDuTest } from "./candidate/vente/IntroDuTest";
import { VenteScreen } from "./candidate/vente/VenteScreen";
import { versPayload, type MesureDeLIntro } from "./candidate/vente/notation";
import type { ConnVerdict, CurrentSession, ProfileFormValues, SessionInfo } from "./types";

/**
 * 🩸 Deux écrans ont disparu le 18-08-2026, sur demande de Kayla : « après
 * [les vérifs], il y a une sorte d'exemple message. Ça supprime cette page-là.
 * Après ça, il passe directement au formulaire et après le formulaire, il fait
 * le test. »
 *
 *   - `welcome`   — « Bienvenue », qui présentait le poste avec UN EXEMPLE ;
 *   - `formation` — « Le brief », un mini-chat d'exemples avant l'épreuve.
 *
 * ⚠️ CE QUI N'EST PAS PARTI AVEC EUX : l'avertissement « contenu adulte », qui
 * a déménagé sur le formulaire (`ProfileFormScreen`). C'est la seule phrase
 * qui dit au candidat ce qu'on va lui demander d'écrire — et c'est maintenant
 * le PREMIER écran du parcours, donc il le lit plus tôt qu'avant.
 */
type ScreenName =
  | "loading"
  | "error"
  | "profile"
  /** Candidature déposée, invitation partie — le point final du parcours
   *  PUBLIC. L'épreuve ne s'ouvre PLUS d'ici : elle attend le lien marqué que
   *  Kayla poste sur Discord (cf. `modeTestDemande`). */
  | "depose"
  /** Profil fait, épreuve pas encore ouverte — l'attente pendant qu'on résout
   *  la session. Sur téléphone, c'est le mur qui s'affiche à sa place. */
  | "pret"
  | "techcheck"
  /** L'introduction : les leçons et la vidéo de Kayla (06-10-2026). */
  | "intro"
  | "vente"
  | "end"
  /** `&connexion=1` : la vérification de connexion SEULE (Kayla, 07-10-2026). */
  | "connexion"
  /** Son résultat, relu chez le serveur — qui seul tranche le verdict. */
  | "connexion_finie";

/**
 * Les écrans qui EXIGENT un ordinateur — et eux seuls.
 *
 * `profile` n'en est pas : c'est tout le correctif du 19-08, il doit se
 * remplir depuis un téléphone. `end` non plus : quelqu'un qui rouvre son lien
 * après être allé au bout doit lire « c'est terminé », pas un mur qui lui
 * demande de recommencer sur un PC. `error` non plus, sinon un lien mort est
 * indiscernable d'un mauvais appareil.
 */
const ECRANS_PC: ReadonlySet<ScreenName> = new Set<ScreenName>([
  "pret",
  "techcheck",
  "intro",
  "vente",
  "connexion",
]);

/**
 * La conversion qui manquait entre le fil et la page.
 *
 * `GET /state` renvoie toujours un objet `session`, vide plutôt que `null`
 * quand rien n'est ouvert (cf. `CurrentSession`). Le reste de la page, lui,
 * veut la seule chose qui l'intéresse : il y a une session, ou il n'y en a
 * pas. C'est ici que ça se tranche — et nulle part ailleurs, sinon la garde
 * de vérité qui suit redevient un `if` qui ne peut pas être faux.
 */
function sessionOuverte(s: CurrentSession): SessionInfo | null {
  const { session_id, session_index, status } = s;
  if (session_id === null || session_index === null || status === null) return null;
  return { session_id, session_index, status, answered: s.answered, total: s.total };
}

/**
 * Le marqueur qui ouvre l'ÉPREUVE — et rien d'autre ne l'ouvre.
 *
 * 🩸 Décision de Kayla (19-08-2026) : « c'est sur Discord que je vais mettre le
 * lien du test. Ils seront obligés de le consulter sur Discord pour pouvoir
 * faire le test. » Sans ce marqueur, rouvrir son lien de candidature redonne
 * l'accusé de réception, jamais le test.
 *
 * 🪤 Il se lit par `useSearchParams`, PAS par `window.location.search` : sous
 * un `MemoryRouter` la seconde forme rend toujours vide, donc le garde-fou
 * serait invérifiable — et un banc qui ne peut pas rougir ne garde rien.
 *
 * ⚠️ C'EST UN GARDE-FOU SOUPLE, PAS UNE SERRURE. Un candidat qui devine
 * `?test=1` passe. Ça retire le chemin ACCIDENTEL — celui qu'on empruntait
 * sans le vouloir en rechargeant la page —, pas le chemin volontaire. Le
 * fermer pour de bon demanderait que l'épreuve reconnaisse le membre par son
 * compte cockpit, donc qu'elle soit authentifiée : un autre chantier, écarté
 * ce jour-là au profit du plus simple.
 */
export const MARQUEUR_TEST = "test";

/**
 * Le marqueur qui ROUVRE l'épreuve à quelqu'un qui l'a déjà finie.
 *
 * 🩸 Kayla, 07-10-2026 : tout le monde passe le NOUVEAU test, et un
 * non-qualifié peut le repasser sur ordinateur. Or un candidat `submitted`
 * (l'ancien test, passé en août sur téléphone) qui ouvrait `?test=1` lisait
 * « déjà allé au bout » et ne pouvait rien faire. Avec `&reprise=1` — que
 * Francis met dans le lien qu'il donne (`/internal/regie/lien-test`) — il
 * repart : mur PC, vérif technique si pas déjà faite, intro, simulation, sur
 * une NOUVELLE session (`POST /session`), où son résultat se poste.
 *
 * Sans ce marqueur, rien ne change : `submitted` rend l'écran de fin.
 */
export const MARQUEUR_REPRISE = "reprise";

/**
 * `?test=1&connexion=1` : refaire la vérification de connexion, et rien
 * d'autre (Kayla, 07-10-2026). Tsou, refusé sur 2 312 ms mesurés pendant un
 * build du serveur, n'a pas à repasser les dix minutes d'épreuve : Cyrano
 * (`/internal/regie/lien-test`, `refaire: "connexion"`) met ce marqueur quand
 * la connexion est la SEULE raison du refus. La nouvelle mesure remplace
 * l'ancienne, et la qualification se relit dessus.
 */
export const MARQUEUR_CONNEXION = "connexion";

/** Le message d'un 409 à l'ouverture de l'épreuve : le serveur refuse une
 *  nouvelle session, et le candidat ne peut rien y faire seul. */
export const MESSAGE_SESSION_REFUSEE =
  "Ton test n'a pas pu s'ouvrir. Écris à Francis dans ton ticket Discord : il te débloque.";

/**
 * Les écrans où le profil est DÉJÀ en base — donc où l'invitation peut partir.
 *
 * 🩸 Décision de Kayla (19-08) : « au lieu d'attendre qu'il ait terminé tout le
 * test, on l'invite à l'organisation directement. Comme ça, après, ils
 * rejoignent le Discord et moi, sur le Discord, je m'occupe de leur faire faire
 * le test dans tous les cas. » L'invitation attendait le SEUIL du test — que
 * les candidats venus du mobile n'atteignaient jamais, puisqu'il faut un
 * ordinateur pour le passer. Elle part maintenant dès le formulaire.
 *
 * ⚠️ `end` n'en est PAS, et ce n'est pas un oubli : l'écran de fin fait son
 * propre `claim` et affiche son propre statut (« Je n'ai rien reçu », renvoi).
 * L'y ajouter ferait partir deux appels concurrents pour rien.
 */
const ECRANS_PROFIL_DEPOSE: ReadonlySet<ScreenName> = new Set<ScreenName>([
  "depose",
  "pret",
  "techcheck",
  "intro",
  "vente",
]);

/** Ce que le candidat peut lire de son invitation sur le mur PC.
 *
 *  `urlActivation` = le lien d'acceptation WorkOS, celui que porte l'e-mail.
 *  `null` quand WorkOS ne l'a pas rendu : l'écran retombe alors sur la seule
 *  promesse d'e-mail, sans bouton mort. */
export type EtatInvitation =
  | { kind: "en_cours" }
  | { kind: "ok"; email: string; urlActivation: string | null }
  /** L'invitation a déjà été acceptée — le candidat revient sur son lien après
   *  être entré. Il n'y a plus rien à envoyer ni à activer, seulement à se
   *  connecter. Sans cet état l'écran lui annonçait un ÉCHEC d'envoi. */
  | { kind: "deja_membre" }
  | { kind: "echec" };

interface State {
  screen: ScreenName;
  firstName: string | null;
  /** E-mail du candidat — affiché sur l'écran de fin (« invitation envoyée
   *  à … »). Vient du profil soumis (flux vivant) ou de GET /state (reprise). */
  email: string | null;
  sessionId: string | null;
  errorMessage: string | null;
  /** Le titre de l'écran d'erreur — « Ce lien n'est plus valide. » par défaut. */
  errorTitle: string | null;
  /** Ce que `GET /state` a répondu pour une candidature déjà profilée. Gardé
   *  tel quel et relu par l'ouverture de l'épreuve, qui n'a lieu QUE sur
   *  ordinateur — d'où la séparation en deux temps. */
  reprise: { session: SessionInfo | null; techcheckFait: boolean } | null;
  /** Ce que l'intro a mesuré (temps passé, vidéo vue) — part avec le résultat. */
  intro: MesureDeLIntro | null;
  /** Le verdict de la vérification de connexion seule, relu chez le serveur. */
  connexion: ConnVerdict | null;
}

const initialState: State = {
  screen: "loading",
  firstName: null,
  email: null,
  sessionId: null,
  errorMessage: null,
  errorTitle: null,
  reprise: null,
  intro: null,
  connexion: null,
};

type Action =
  | { type: "error"; message: string; titre?: string }
  | { type: "need_profile" }
  | { type: "profil_depose"; firstName: string | null; email: string | null }
  | { type: "already_submitted"; firstName: string | null; email: string | null }
  | {
      type: "reprise_possible";
      firstName: string | null;
      email: string | null;
      session: SessionInfo | null;
      techcheckFait: boolean;
    }
  | { type: "epreuve_ouverte"; sessionId: string; techcheckFait: boolean }
  | {
      type: "profile_done";
      firstName: string;
      email: string | null;
      sessionId: string;
    }
  | { type: "techcheck_done" }
  | { type: "intro_done"; mesure: MesureDeLIntro }
  | { type: "go_end"; firstName: string | null }
  | { type: "connexion_seule"; firstName: string | null }
  | { type: "connexion_finie"; verdict: ConnVerdict | null };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "error":
      return { ...state, screen: "error", errorMessage: action.message, errorTitle: action.titre ?? null };
    case "need_profile":
      return { ...state, screen: "profile" };
    case "profil_depose":
      return {
        ...state,
        screen: "depose",
        firstName: action.firstName,
        email: action.email,
      };
    case "already_submitted":
      return {
        ...state,
        screen: "end",
        firstName: action.firstName,
        email: action.email,
      };
    case "reprise_possible":
      return {
        ...state,
        screen: "pret",
        firstName: action.firstName,
        email: action.email,
        reprise: { session: action.session, techcheckFait: action.techcheckFait },
      };
    case "epreuve_ouverte":
      return {
        ...state,
        // La vérif technique n'est PAS rejouée si elle a déjà eu lieu : elle
        // mesure la machine, pas la session, et la refaire à chaque coupure
        // ferait perdre dix secondes pour une donnée qu'on a déjà.
        screen: action.techcheckFait ? "intro" : "techcheck",
        sessionId: action.sessionId,
      };
    case "profile_done":
      return {
        ...state,
        // 🩸 Le formulaire s'arrête ICI, sur tous les appareils. Il enchaînait
        // sur la vérif technique jusqu'au 19-08-2026 ; Kayla : « une fois
        // qu'il a rempli le formulaire, on l'invite dans l'organisation. Il a
        // juste une page qui lui dit qu'une invitation lui a été envoyée […]
        // et c'est sur Discord que je vais mettre le lien du test. »
        screen: "depose",
        firstName: action.firstName,
        email: action.email,
        sessionId: action.sessionId,
      };
    case "techcheck_done":
      return { ...state, screen: "intro" };
    case "intro_done":
      return { ...state, screen: "vente", intro: action.mesure };
    case "go_end":
      return { ...state, screen: "end", firstName: action.firstName };
    case "connexion_seule":
      return { ...state, screen: "connexion", firstName: action.firstName };
    case "connexion_finie":
      return { ...state, screen: "connexion_finie", connexion: action.verdict };
    default:
      return state;
  }
}

/** ⚠️ Les `step` sont des INDEX dans la jauge de `PhoneFrame`, et leur nombre
 *  doit rester d'accord avec `TOTAL_STEPS` (4). Le formulaire est passé en
 *  tête le 19-08 : ce sont les NUMÉROS qui ont échangé, pas leur compte. Une
 *  jauge qui n'atteint jamais son bout se lit comme un parcours inachevé. */
const SCREEN_META: Record<ScreenName, { subtitle: string; step: number }> = {
  loading: { subtitle: "Chargement…", step: 0 },
  error: { subtitle: "Lien invalide", step: 0 },
  profile: { subtitle: "Tes infos", step: 0 },
  depose: { subtitle: "Candidature envoyée", step: 1 },
  pret: { subtitle: "Chargement…", step: 1 },
  techcheck: { subtitle: "Vérification", step: 1 },
  intro: { subtitle: "Avant de commencer", step: 2 },
  vente: { subtitle: "Fais-le payer", step: 2 },
  end: { subtitle: "Terminé", step: 3 },
  connexion: { subtitle: "Vérification", step: 1 },
  connexion_finie: { subtitle: "Vérification", step: 3 },
};

export function RegieTestCandidatePage() {
  const { token = "" } = useParams<{ token: string }>();
  const [parametres] = useSearchParams();
  /** Le lien que Kayla poste sur Discord porte `?test=1` ; lui seul ouvre
   *  l'épreuve (cf. `MARQUEUR_TEST`). */
  const modeTest = parametres.get(MARQUEUR_TEST) === "1";
  /** `?test=1&reprise=1` : un candidat qui a déjà fini repasse l'épreuve. */
  const modeReprise = modeTest && parametres.get(MARQUEUR_REPRISE) === "1";
  /** `?test=1&connexion=1` : la vérification de connexion seule. */
  const modeConnexion = modeTest && parametres.get(MARQUEUR_CONNEXION) === "1";
  const [state, dispatch] = useReducer(reducer, initialState);
  const estOrdinateur = useEstOrdinateur();

  useEffect(() => {
    initPixel();
  }, []);

  // ── Temps 1 : lire l'état. Aucun effet de bord, donc sans danger depuis un
  //    téléphone — c'est justement là que le candidat arrive maintenant.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await candidateApi.getState(token);
        if (cancelled) return;

        // La connexion seule : il a déjà son test, il ne rejoue rien d'autre.
        if (modeConnexion && status.has_profile) {
          dispatch({ type: "connexion_seule", firstName: status.first_name });
          return;
        }

        // Déjà allé au bout : on ne rejoue pas l'épreuve. `submitted` suffit —
        // il n'y a plus de seuil, donc plus de « raté, retente » à distinguer
        // d'un passage terminé.
        if (status.status === "submitted" && !modeReprise) {
          dispatch({
            type: "already_submitted",
            firstName: status.first_name,
            email: status.email ?? null,
          });
          return;
        }
        if (!status.has_profile) {
          dispatch({ type: "need_profile" });
          return;
        }

        // 🚪 Le profil est là. L'épreuve ne s'ouvre pourtant que sur le lien
        // marqué (cf. `modeTestDemande`) : sans lui, on rend l'accusé de
        // réception, et le candidat attend son lien sur le Discord.
        if (!modeTest) {
          dispatch({
            type: "profil_depose",
            firstName: status.first_name,
            email: status.email ?? null,
          });
          return;
        }

        dispatch({
          type: "reprise_possible",
          firstName: status.first_name,
          email: status.email ?? null,
          session: sessionOuverte(status.session),
          // Seul `POST /device` renseigne ce champ, et seule la vérif
          // technique l'appelle : il vaut donc exactement « la vérif a déjà
          // eu lieu ».
          // 🩸 Mais une vérif RATÉE (téléphone, connexion « bad ») se refait à
          // la reprise : sinon le refus est éternel. 07-10-2026, Tsou : refusé
          // sur une latence de 2 312 ms, il a repassé l'épreuve trois fois
          // sans que sa connexion soit jamais remesurée.
          techcheckFait: status.device_class === "computer" && status.connection?.probe !== "bad",
        });
      } catch (err) {
        if (cancelled) return;
        dispatch({
          type: "error",
          message: err instanceof Error ? err.message : "Ce lien n'est plus valide.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, modeTest, modeReprise, modeConnexion]);

  // ── Temps 2 : ouvrir l'épreuve. SUR ORDINATEUR SEULEMENT (cf. l'en-tête) —
  //    c'est ici que se trouve le seul appel qui écrit côté serveur.
  const reprise = state.reprise;
  useEffect(() => {
    if (!estOrdinateur) return;
    if (state.screen !== "pret" || reprise === null) return;

    let cancelled = false;
    (async () => {
      try {
        // 🪤 L'épreuve REPART DU DÉBUT — elle ne s'autosauvegarde pas, c'est
        // une scène de dix minutes qui se joue d'un trait. Reprendre la
        // session en cours sert à ne pas en ouvrir une deuxième (le serveur
        // la refuserait), pas à reprendre le fil exact.
        const session =
          reprise.session && reprise.session.status === "in_progress"
            ? reprise.session
            : await candidateApi.startSession(token);
        if (cancelled) return;
        dispatch({
          type: "epreuve_ouverte",
          sessionId: session.session_id,
          techcheckFait: reprise.techcheckFait,
        });
      } catch (err) {
        if (cancelled) return;
        // 🩸 Un 409 n'est PAS un lien mort : le serveur refuse d'ouvrir une
        // session de plus. Le candidat ne peut rien y faire seul — on lui dit
        // à qui écrire, au lieu de « Ce lien n'est plus valide. ».
        if (err instanceof ApiError && err.status === 409) {
          dispatch({ type: "error", titre: "Ton test n'a pas pu s'ouvrir.", message: MESSAGE_SESSION_REFUSEE });
          return;
        }
        dispatch({
          type: "error",
          message: err instanceof Error ? err.message : "Ce lien n'est plus valide.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estOrdinateur, state.screen, reprise, token]);

  // ── L'invitation part dès que le profil est en base (cf.
  //    `ECRANS_PROFIL_DEPOSE`). Best-effort : son échec ne doit RIEN bloquer —
  //    le candidat a fait sa part, et l'écran de fin réessaiera de toute façon.
  const claim = useAction(convexApi.candidateInvite.claim);
  const [invitation, setInvitation] = useState<EtatInvitation>(
    IS_MOCK_MODE
      ? {
          kind: "ok",
          email: "camille@email.com",
          urlActivation: "https://exemple.authkit.app/invite?invitation_token=maquette",
        }
      : { kind: "en_cours" },
  );
  const invitationLancee = useRef(false);

  useEffect(() => {
    if (IS_MOCK_MODE || invitationLancee.current) return;
    if (!ECRANS_PROFIL_DEPOSE.has(state.screen)) return;
    // Une seule fois par chargement de page. `claim` est idempotent côté
    // serveur (mode "ensure" ne renvoie pas d'e-mail sur une invitation déjà
    // en attente) — cette garde évite juste l'appel inutile à chaque rendu.
    invitationLancee.current = true;
    claim({ token, mode: "ensure" })
      .then((r) =>
        setInvitation(
          r.status === "deja_membre"
            ? { kind: "deja_membre" }
            : { kind: "ok", email: r.email, urlActivation: r.acceptUrl },
        ),
      )
      .catch((err) => {
        setInvitation({ kind: "echec" });
        console.error(
          "[regie-test] l'invitation a l'organisation n'est PAS partie —" +
            " le candidat ne recevra rien et n'entrera pas dans l'organisation",
          err,
        );
      });
  }, [state.screen, token, claim]);

  const profileMutation = useMutation({
    mutationFn: (values: ProfileFormValues) => candidateApi.postProfile(token, values),
    onSuccess: (deck, values) => {
      // Le backend GÈLE la session 1 et en renvoie l'`session_id` avec le deck
      // (cf. `ProfileResponse`) — on la garde sous le coude, sans POST
      // /session redondant (`startSession` est réservé aux REPRISES ; l'appeler
      // sur une session en cours renverrait 409).
      dispatch({
        type: "profile_done",
        firstName: values.first_name,
        email: values.email || null,
        sessionId: deck.session_id,
      });
      // « candidature envoyée » — l'événement sur lequel Meta optimise.
      // 🩸 07-10-2026 : il partait au CLIC sur « Postuler » (landing), alors
      // que deux cliqueurs sur trois n'envoient jamais le formulaire ; Meta
      // apprenait à ramener des cliqueurs. Il part maintenant ici, une fois le
      // profil ACCEPTÉ par le serveur.
      trackPixel("Lead");
    },
  });

  const meta = SCREEN_META[state.screen];

  // Le mur PC ne couvre plus que les écrans qui exigent vraiment un ordinateur
  // (cf. `ECRANS_PC`). Il ne consomme rien : le jeton reste valable et l'écran
  // le rend au candidat.
  // L'accusé de réception se lit sur TOUS les appareils — il n'est pas un
  // repli mobile mais la fin du parcours public.
  if (state.screen === "depose") {
    return (
      <EcranCandidatureDeposee invitation={invitation} />
    );
  }

  if (!estOrdinateur && ECRANS_PC.has(state.screen)) {
    return <EcranOrdinateur />;
  }

  // L'épreuve a son propre cadre : pleine largeur, palette du poste, fil qui
  // scrolle seul. C'est la demande de Kayla — le test doit se passer « comme
  // en conditions réelles », donc dans l'écran du poste et pas dans la carte
  // étroite du reste de la candidature.
  if (state.screen === "intro") {
    return (
      <CadreDuPoste subtitle={meta.subtitle} stepIndex={meta.step}>
        <IntroDuTest onFini={(mesure) => dispatch({ type: "intro_done", mesure })} />
      </CadreDuPoste>
    );
  }

  if (state.screen === "vente") {
    return (
      <CadreDuPoste subtitle={meta.subtitle} stepIndex={meta.step}>
        <VenteScreen
          intro={state.intro}
          onFini={(resultat) => {
            // Le résultat part AVANT l'écran de fin, et son échec ne bloque
            // toujours rien pour le candidat : il est allé au bout, il a fait
            // sa part, et lui montrer une erreur réseau à la dernière seconde
            // ne rendrait la partie à personne.
            //
            // 🩸 MAIS IL NE SE TAIT PLUS. Ce `.catch()` était vide, et c'est
            // ce qui a rendu une panne totale invisible : le 18-08, le serveur
            // refusait CHAQUE résultat (422 sur le champ `gestes`, absent de
            // son schéma), la session restait `in_progress`, aucune invitation
            // ne partait — et l'écran affichait « Félicitations » par-dessus.
            // Trouvé en déroulant le parcours à la main, pas par un test.
            if (state.sessionId) {
              candidateApi
                .postVente(token, state.sessionId, versPayload(resultat))
                .catch((err) => {
                  console.error(
                    "[regie-test] le résultat de l'épreuve n'a PAS été enregistré —" +
                      " la candidature restera muette et l'invitation ne partira pas",
                    err,
                  );
                });
            }
            // « test terminé » — le candidat est allé au bout (event de
            // qualité pour l'optimisation Meta).
            trackPixel("CompleteRegistration");
            dispatch({ type: "go_end", firstName: state.firstName });
          }}
        />
      </CadreDuPoste>
    );
  }

  return (
    <PhoneFrame subtitle={meta.subtitle} stepIndex={meta.step}>
      {(state.screen === "loading" || state.screen === "pret") && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10">
          <Spinner taille="lg" />
        </div>
      )}

      {state.screen === "error" && (
        <div className="flex flex-1 flex-col justify-center">
          <Vide
            icone={<AlertCircle aria-hidden className="h-9 w-9" />}
            titre={state.errorTitle ?? "Ce lien n'est plus valide."}
            texte={
              state.errorMessage ||
              "Vérifie que tu as bien copié le lien en entier, ou demande un nouveau lien."
            }
          />
        </div>
      )}

      {state.screen === "techcheck" && (
        <TechCheckScreen token={token} onDone={() => dispatch({ type: "techcheck_done" })} />
      )}

      {state.screen === "connexion" && (
        <TechCheckScreen
          token={token}
          onDone={() => {
            candidateApi
              .getState(token)
              .then((s) => dispatch({ type: "connexion_finie", verdict: s.connection.probe }))
              .catch(() => dispatch({ type: "connexion_finie", verdict: null }));
          }}
        />
      )}

      {state.screen === "connexion_finie" && (
        <ConnexionFinie
          verdict={state.connexion}
          onRefaire={() => dispatch({ type: "connexion_seule", firstName: state.firstName })}
        />
      )}

      {state.screen === "profile" && (
        <ProfileFormScreen
          submitting={profileMutation.isPending}
          errorMessage={profileMutation.error instanceof Error ? profileMutation.error.message : null}
          onSubmit={(values) => profileMutation.mutate(values)}
        />
      )}

      {state.screen === "end" && (
        <EndScreen firstName={state.firstName} email={state.email} token={token} />
      )}
    </PhoneFrame>
  );
}

/** La fin de la vérification de connexion seule : ce qu'il fait maintenant. */
function ConnexionFinie({ verdict, onRefaire }: { verdict: ConnVerdict | null; onRefaire: () => void }) {
  if (verdict === "good" || verdict === "ok") {
    return (
      <div className="flex flex-1 flex-col justify-center">
        <Vide
          icone={<CheckCircle2 aria-hidden className="h-10 w-10 text-valide" />}
          titre="Connexion validée."
          texte="Retourne sur Discord et écris « c'est fait » à Francis : il te met sur ton shift."
        />
      </div>
    );
  }
  return (
    <div className="flex flex-1 flex-col justify-center">
      <Vide
        icone={<AlertCircle aria-hidden className="h-9 w-9" />}
        titre={verdict === "bad" ? "Ta connexion est encore trop lente." : "La mesure n'a pas pu s'enregistrer."}
        texte="Essaie un autre réseau (le partage de connexion de ton téléphone, un autre wifi), ferme les téléchargements et les vidéos en cours, puis refais la vérification."
        action={
          <Button variant="primary" size="lg" className="min-h-12 px-6 text-[15px]" onClick={onRefaire}>
            Refaire la vérification
          </Button>
        }
      />
    </div>
  );
}
